import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import DlsLink from "@galaxy-io/dls/links/Link";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Text from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { personReviewKey, sourceName } from "@gtm/contracts";
import {
  ArrowSquareOutIcon,
  LinkIcon,
  PencilSimpleIcon,
  ProhibitIcon,
  TrashIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import {
  DrawerExternalLink,
  DrawerList,
  DrawerRow,
  DrawerSection,
  DrawerStack,
} from "@/components/workspace/drawer-content";
import { MenuItem, OverflowMenu } from "@/components/workspace/overflow-menu";
import { RecordAudit } from "@/components/workspace/record-audit";
import { RecordQueries } from "@/components/workspace/record-queries";
import { SourceLabel } from "@/components/workspace/source-icon";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { ActivityFeed } from "@/features/activity/people-feed";
import { ReviewStatusEditor, useReviews } from "@/features/activity/review-actions";
import { ReviewHistory } from "@/features/activity/review-history";
import { SendMenu } from "@/features/activity/send-menu";
import { CompanyLink } from "@/features/companies/company-link";
import {
  ExclusionActions,
  ExclusionStatus,
  useExclusionContext,
} from "@/features/exclusions/exclusion-action";
import { DeleteEntity } from "@/features/people/delete-entity";
import { EntityAssignment } from "@/features/people/entity-assignment";
import { Outbound } from "@/features/people/outbound";
import { Avatar } from "@/features/people/profile-drawer";
import { EntityTags } from "@/features/tags/tag-controls";
import { apiClient, unwrap } from "@/lib/api-client";
import { personQueries } from "@/lib/record-queries";
import { date, safeUrl } from "@/lib/utils";
import { PersonEmails } from "./person-emails";

export function PersonInspector({
  id,
  onClose,
  restoreFocus,
}: {
  id: string | null;
  onClose: () => void;
  restoreFocus?: () => void;
}) {
  const retained = useRetainedValue(id);
  return (
    <PersonDetails
      key={retained}
      id={retained ?? ""}
      open={!!id}
      onClose={onClose}
      restoreFocus={restoreFocus}
    />
  );
}
function PersonDetails({
  id,
  open,
  onClose,
  restoreFocus,
}: {
  id: string;
  open: boolean;
  onClose: () => void;
  restoreFocus?: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const result = useQuery({
    queryKey: ["person", id],
    enabled: open,
    queryFn: ({ signal }) =>
      unwrap(apiClient().GET("/people/{id}", { params: { path: { id } }, signal })),
    retry: false,
  });
  const person = result.data;
  const error = result.error?.message;
  const { params, update } = useUrlState();
  const selectedTab = params.get("personTab");
  const tab =
    selectedTab === "activity" || selectedTab === "audit" || selectedTab === "queries"
      ? selectedTab
      : "overview";
  const setTab = (value: string) => update({ personTab: value === "overview" ? null : value });
  const key = personReviewKey(id),
    reviews = useReviews(open ? [key] : []),
    exclusion = useExclusionContext("person", id, open);
  const excluded = !!exclusion.data?.reasons.length;
  return (
    <SurfaceDialog
      open={open}
      drawer
      flush
      title="Person"
      headerTitle={person?.name || person?.login || "Person"}
      headerDescription={
        person
          ? person.provider
            ? `@${person.login} · ${sourceName(person.provider)}`
            : "Manually created"
          : "Loading profile…"
      }
      headerLeading={person ? <Avatar src={person.avatar} name={person.login} /> : undefined}
      onClose={onClose}
      restoreFocus={restoreFocus}
      headerActions={
        <>
          {person && <Outbound personId={id} />}
          {person && <SendMenu keys={[key]} disabled={excluded || !exclusion.data} textOnly />}
          {person && safeUrl(person.url) && (
            <Button
              size={ButtonSize.SMALL}
              ariaLabel={`View on ${sourceName(person.provider || "github")}`}
              icon={ArrowSquareOutIcon}
              variant={ButtonVariant.TERTIARY}
              tooltip={`View on ${sourceName(person.provider || "github")}`}
              href={safeUrl(person.url) || "#"}
              isExternal
            />
          )}
          {person && (
            <ExclusionActions
              kind="person"
              id={id}
              trigger={(open, excluded) => (
                <OverflowMenu size={ButtonSize.SMALL} label="More actions">
                  {(close) => (
                    <>
                      <MenuItem
                        icon={PencilSimpleIcon}
                        label="Edit"
                        onClick={() => {
                          close();
                          update({ personEdit: "1" });
                        }}
                      />
                      <MenuItem
                        icon={ProhibitIcon}
                        label={excluded ? "Excluded" : "Exclude…"}
                        onClick={() => {
                          close();
                          open();
                        }}
                      />
                      <MenuItem
                        icon={TrashIcon}
                        label="Delete person"
                        onClick={() => {
                          close();
                          setDeleteOpen(true);
                        }}
                        isDestructive
                      />
                    </>
                  )}
                </OverflowMenu>
              )}
            />
          )}
        </>
      }
    >
      {person && (
        <DeleteEntity
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          kind="person"
          id={id}
          name={person.name || person.login}
          onDeleted={onClose}
        />
      )}
      {error ? (
        <QueryState error={error} onRetry={() => void result.refetch()} />
      ) : !person ? (
        <QueryState loadingMessage="Loading person…" />
      ) : (
        <>
          <Tabs
            ariaLabel="Person sections"
            value={tab}
            onChange={setTab}
            items={[
              { id: "overview", label: "Overview" },
              { id: "activity", label: "Activity" },
              { id: "queries", label: "Queries" },
              { id: "audit", label: "Audit" },
            ]}
          />
          {tab === "overview" ? (
            <DrawerStack>
              <DrawerList>
                <DescriptionList
                  items={[
                    { label: "Tags", value: <EntityTags kind="person" id={id} asValue /> },
                    {
                      label: "Assigned to",
                      value: (
                        <EntityAssignment
                          kind="person"
                          id={id}
                          assignedTo={person.assignedTo}
                          asValue
                        />
                      ),
                    },
                    {
                      label: "Status",
                      value: (
                        <>
                          <ReviewStatusEditor
                            review={reviews.states[key]}
                            busy={reviews.busy || !reviews.loaded}
                            onAction={(action) => reviews.act([key], action)}
                          />
                        </>
                      ),
                    },
                    {
                      label: "Source",
                      value: (
                        <>
                          <SourceLabel
                            source={person.provider || "manual"}
                            label={sourceName(person.provider || "manual")}
                          />
                        </>
                      ),
                    },
                    {
                      label: "Discovered",
                      value: date(person.firstSeenAt),
                      family: FontFamily.MONO,
                    },
                  ]}
                />
              </DrawerList>
              <ExclusionStatus kind="person" id={id} />
              <DrawerSection header="Profile" icon={UserIcon} defaultIsOpen>
                <DescriptionList
                  items={[
                    {
                      label: "Company",
                      value:
                        person.companyId && person.company ? (
                          <CompanyLink id={person.companyId}>{person.company}</CompanyLink>
                        ) : (
                          person.company || "—"
                        ),
                    },
                    { label: "Domain", value: person.domain || "—" },
                    { label: "Location", value: person.location || "—" },
                  ]}
                />
                {person.bio && <Text>{person.bio}</Text>}
              </DrawerSection>
              <DrawerSection header="Social" icon={LinkIcon} defaultIsOpen>
                {(
                  person.identities || [
                    {
                      provider: person.provider || "github",
                      url: person.url,
                      handle: person.login,
                    },
                  ]
                )
                  .filter((identity) => safeUrl(identity.url || ""))
                  .map((identity) => (
                    <DrawerRow
                      key={`${identity.provider}:${identity.url}`}
                      label={sourceName(identity.provider)}
                    >
                      <DlsLink href={safeUrl(identity.url || "") || undefined} isExternal>
                        <Text as="span">
                          {person.identities ? identity.url : `@${identity.handle}`}
                        </Text>
                      </DlsLink>
                    </DrawerRow>
                  ))}
                <PersonEmails
                  key={id}
                  id={id}
                  username={
                    person.identities?.find((identity) => identity.provider === "github")?.handle ??
                    (person.provider === "github" ? person.login : undefined)
                  }
                />
                <DrawerRow label="Phone number">{person.phoneNumber || "—"}</DrawerRow>
                {person.twitterUsername && (
                  <DrawerRow label="X">
                    <DlsLink
                      href={`https://x.com/${encodeURIComponent(person.twitterUsername)}`}
                      isExternal
                    >
                      <Text as="span">@{person.twitterUsername}</Text>
                    </DlsLink>
                  </DrawerRow>
                )}
                {person.socialAccounts
                  ?.filter(
                    (account) =>
                      safeUrl(account.url) &&
                      !(
                        person.twitterUsername &&
                        ["twitter", "x"].includes(account.provider.toLowerCase())
                      ),
                  )
                  .map((account) => (
                    <DrawerRow
                      key={account.url}
                      label={
                        account.provider === "twitter"
                          ? "X"
                          : account.provider === "linkedin"
                            ? "LinkedIn"
                            : account.provider.charAt(0).toUpperCase() + account.provider.slice(1)
                      }
                    >
                      <DlsLink href={safeUrl(account.url)!} isExternal>
                        <Text as="span">{account.url}</Text>
                      </DlsLink>
                    </DrawerRow>
                  ))}
                {safeUrl(person.website) && (
                  <DrawerRow label="Website">
                    <DrawerExternalLink
                      href={safeUrl(person.website)!}
                      target="_blank"
                      rel="noreferrer"
                      title={person.website || undefined}
                    >
                      <Text as="span">{person.website}</Text>
                    </DrawerExternalLink>
                  </DrawerRow>
                )}
              </DrawerSection>
            </DrawerStack>
          ) : tab === "queries" ? (
            <DrawerStack>
              <RecordQueries queries={personQueries(id)} />
            </DrawerStack>
          ) : tab === "audit" ? (
            <DrawerStack>
              <RecordAudit kind="person" id={id} />
              <ReviewHistory recordKey={key} />
            </DrawerStack>
          ) : (
            <ActivityFeed
              timeline
              initial={person.activity}
              query={{ person: id }}
              showActions={false}
            />
          )}
        </>
      )}
    </SurfaceDialog>
  );
}
export function PeopleOverlay() {
  const { params, update } = useUrlState();
  const id = params.get("personId");
  return (
    <PersonInspector
      id={id === "new" ? null : id}
      restoreFocus={() =>
        document
          .querySelector<HTMLAnchorElement>(`a[data-person-id="${CSS.escape(id || "")}"]`)
          ?.focus()
      }
      onClose={() => update({ personId: null, person: null, personTab: null })}
    />
  );
}
